const EASTERN_TIME_ZONE = 'America/New_York';
const MS_IN_A_DAY = 24 * 60 * 60 * 1000;
const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Normalize a date-like value to an Eastern calendar date string.
// This keeps availability labels aligned with the product's EST-based rules.
const getEasternYMD = (dateValue) => {
    if (typeof dateValue === 'string') {
        const trimmedValue = dateValue.trim();
        // Preserve date-only strings as-is to avoid UTC/local timezone shifts.
        if (YMD_PATTERN.test(trimmedValue)) {
            return trimmedValue;
        }
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) {
        return null;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: EASTERN_TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });

    const parts = formatter.formatToParts(date);
    const dateMap = {};

    for (const part of parts) {
        dateMap[part.type] = part.value;
    }

    return `${dateMap.year}-${dateMap.month}-${dateMap.day}`;
};

// Convert a YYYY-MM-DD string into a UTC midnight timestamp so day-diff math
// can compare Eastern calendar dates without depending on the browser's local zone.
const ymdToUtc = (ymd) => {
    const [year, month, day] = ymd.split('-').map((value) => Number(value));
    return Date.UTC(year, month - 1, day);
};

// Public helper used by search and hero components to format a date in Eastern time.
export const getEasternDateString = (dateValue) => getEasternYMD(dateValue);

// Convenience helper for "today" in Eastern time, used when we need a default
// request window that matches the backend's availability calendar.
export const getEasternTodayString = () => getEasternDateString(new Date());

// Compare two timestamps using Eastern calendar days instead of local browser time.
export const getEasternDayDiff = (fromDateValue, toDateValue) => {
    const fromDate = getEasternYMD(fromDateValue);
    const toDate = getEasternYMD(toDateValue);

    if (!fromDate || !toDate) {
        return null;
    }

    return Math.floor((ymdToUtc(toDate) - ymdToUtc(fromDate)) / MS_IN_A_DAY);
};

// --- Added for AHSMKTG-4011 provider carousel availability display ---

export const isWithinOneHour = (now, rawValue) => {
    if (!rawValue) {
        return false;
    }
    // Date-only strings have no time component; sub-hour comparison is meaningless.
    if (typeof rawValue === 'string' && YMD_PATTERN.test(rawValue.trim())) {
        return false;
    }
    const d = new Date(rawValue);
    if (isNaN(d.getTime())) {
        return false;
    }
    const diff = d.getTime() - now.getTime();
    return diff >= 0 && diff <= 3600000;
};

export const formatEasternTime = (rawValue) => {
    if (!rawValue) {
        return null;
    }
    // Date-only strings carry no time component; nothing meaningful to format.
    if (typeof rawValue === 'string' && YMD_PATTERN.test(rawValue.trim())) {
        return null;
    }
    const d = new Date(rawValue);
    if (isNaN(d.getTime())) {
        return null;
    }
    return d.toLocaleTimeString('en-US', {
        timeZone: EASTERN_TIME_ZONE,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

export const formatEasternDate = (rawValue) => {
    if (!rawValue) {
        return null;
    }
    let d;
    if (typeof rawValue === 'string' && YMD_PATTERN.test(rawValue.trim())) {
        // Use noon UTC so the Eastern timezone conversion always lands on the
        // same calendar day (UTC midnight would shift to the prior evening in EDT/EST).
        const [year, month, day] = rawValue.trim().split('-').map(Number);
        d = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    } else {
        d = new Date(rawValue);
    }
    if (isNaN(d.getTime())) {
        return null;
    }
    return d.toLocaleDateString('en-US', {
        timeZone: EASTERN_TIME_ZONE,
        weekday: 'short',
        month: 'short',
        day: '2-digit',
    });
};
